<template>
	<v-card class="ma-2 full-height-card">
		<v-card-title class="py-4">
			<div class="d-flex flex-row align-center justify-space-between font-weight-regular" style="width: 100%">
				<div class="d-flex flex-row align-center">
					<v-text-field
						v-model="search"
						:label="$t('search')"
						append-icon="mdi-magnify"
						single-line
						hide-details
						outlined
						dense
						clearable />
					<v-autocomplete
						class="ml-2"
						v-model="activeFilter"
						:label="$t('active')"
						:items="activeFilterOptions"
						item-text="text"
						item-value="value"
						outlined
						dense
						hide-details
						:menu-props="{ maxHeight: 200, offsetY: true }" />
					<v-autocomplete
						class="ml-2"
						v-model="selectedLocation"
						:disabled="groupedTerraMapping.length === 1"
						dense
						:label="$t('location')"
						:items="groupedTerraMapping"
						item-text="locationName"
						return-object
						outlined
						hide-details
						style="width: 350px"
						:menu-props="{ maxHeight: 200, offsetY: true }" />
				</div>
				<div class="d-flex flex-row align-center justify-end" v-if="selectedLocation">
					<v-select
						dense
						hide-details
						:label="$t('data-coordinator-for-all-users-in-location')"
						outlined
						v-model="selectedLocation.ecpUserInpersonateId"
						style="width: 300px"
						item-text="userName"
						item-value="id"
						:items="ecpUsers.find((loc) => loc.locationId === selectedLocation.locationId)?.ecpUserForTerraDto"
						:menu-props="{ maxHeight: 200, offsetY: true }"
						@change="setECPUser(selectedLocation)" />
					<v-tooltip top>
						<template #activator="{ on, attrs }">
							<span v-on="on" v-bind="attrs" class="ml-4">
								<v-checkbox
									:ripple="false"
									hide-details
									class="mt-0 pt-0"
									:disabled="loading"
									:indeterminate="selectedLocation.enableSendingDataToECP === null"
									v-model="selectedLocation.enableSendingDataToECP"
									:true-value="true"
									:false-value="false"
									@click.stop=""
									@change="flipSendingDataToECP(selectedLocation)" />
							</span>
						</template>
						<span>
							{{
								$t(
									selectedLocation.enableSendingDataToECP
										? 'disable-sending-data-for-all-users-to-ecp-in'
										: 'enable-sending-data-for-all-users-to-ecp-in',
								)
							}}
							{{ selectedLocation.locationName }}
						</span>
					</v-tooltip>
				</div>
			</div>
		</v-card-title>
		<v-card-text style="height: calc(100% - 72px)" class="overflow-y-auto">
			<v-data-table
				:items="filteredTerraMapping"
				:headers="userNameGroupHeaders"
				item-key="userName"
				:items-per-page="-1"
				:loading="loading"
				:loading-text="$t('loading')"
				single-expand
				style="overflow-y: auto"
				fixed-header
				:height="pageHeight(124)"
				dense
				class="data-table-outline">
				<template #item="{ item, expand, isExpanded, index }">
					<tr :class="index % 2 ? 'odd-row' : ''" @click.stop="expand(!isExpanded)">
						<td class="text-left text-subtitle-2">{{ item.userName }}</td>
						<td class="d-flex align-center justify-end">
							<v-select
								dense
								hide-details
								@click.stop
								outlined
								v-model="item.ecpUserInpersonateId"
								style="max-width: 300px"
								class="thin-select"
								item-text="userName"
								item-value="id"
								:items="ecpUsers.find((loc) => loc.locationId === item.locationId)?.ecpUserForTerraDto"
								:menu-props="{ maxHeight: 200, offsetY: true }"
								@change="setECPUser(item)">
								<template #prepend-inner>
									<span class="grey--text text--darken-1">
										{{ $t('coordinator') + ':' }}
									</span>
								</template>
							</v-select>
							<v-tooltip top>
								<template #activator="{ on, attrs }">
									<span v-on="on" v-bind="attrs" class="ml-4">
										<v-checkbox
											:ripple="false"
											hide-details
											class="mt-0 pt-0"
											:disabled="loading"
											v-model="item.enableSendingDataToECP"
											:true-value="true"
											:false-value="false"
											@click.stop=""
											@change="flipSendingDataToECP(item)" />
									</span>
								</template>
								<span>
									{{
										$t(
											item.enableSendingDataToECP
												? 'disable-sending-data-to-ecp-for'
												: 'enable-sending-data-to-ecp-for',
										)
									}}
									{{ item.userName }}
								</span>
							</v-tooltip>
							<v-icon color="primary" class="ml-2">
								{{ isExpanded ? 'mdi-menu-up' : 'mdi-menu-down' }}
							</v-icon>
						</td>
					</tr>
				</template>
				<template #expanded-item="{ item, index }">
					<tr style="cursor: default; background-color: white !important" :class="index % 2 ? 'odd-row' : ''">
						<td colspan="3" class="pa-3">
							<v-data-table
								:items="item.mappings"
								:headers="itemHeaders"
								:items-per-page="-1"
								style="overflow-y: auto"
								hide-default-footer
								fixed-header
								:height="pageHeight(-100)"
								dense
								class="data-table-outline">
								<template #item="{ item, index }">
									<tr :class="index % 2 ? 'odd-row' : ''" @click="openViewDialog(item)">
										<td class="text-left">
											<v-icon :color="item.active ? 'green' : 'red'">
												{{ item.active ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline' }}
											</v-icon>
										</td>
										<td class="text-left">{{ item.terraUserId }}</td>
										<td class="text-left">{{ item.provider }}</td>
										<td class="text-left">
											{{ findUserName(item.ecpUserInpersonateId, item.locationId) || $t('no-data') }}
										</td>
										<td class="text-left">
											{{ item.lastWebhookUpdate ? formatTimestamp(item.lastWebhookUpdate) : $t('no-data') }}
										</td>
										<td class="text-left">{{ formatTimestamp(item.terraMappingCreatedAt) }}</td>
										<td class="text-center">
											<v-icon :color="item.enableSendingDataToECP ? 'green' : 'red'">
												{{ item.enableSendingDataToECP ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline' }}
											</v-icon>
										</td>
									</tr>
								</template>
							</v-data-table>
						</td>
					</tr>
				</template>
			</v-data-table>
		</v-card-text>
		<TerraPatientMappingDialog
			v-if="showViewDialog && selectedTerraMapping"
			:dialog.sync="showViewDialog"
			:selectedTerraMapping="selectedTerraMapping"
			@closeDialog="
				showViewDialog = false;
				selectedTerraMapping = null;
			" />
	</v-card>
</template>
<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { formatTimestamp } from '../../../utils/utilities';
import TerraPatientMappingDialog from '../../dialogs/TerraPatientIntegrationDialog.vue';

export default {
	name: 'PatientTerraIntegration',
	components: { TerraPatientMappingDialog },
	data () {
		return {
			deletingIds: [],
			deleteId: null,
			showDeleteDialog: false,
			loading: true,
			activeFilter: null,
			selectedTerraMapping: null,
			showViewDialog: false,
			selectedLocation: null,
			search: '',
		};
	},
	computed: {
		...mapState({
			terraUserMapping: (state) => state.terra.terraUserMapping,
			ecpUsers: (state) => state.terra.ecpUsers,
		}),
		...mapGetters({
			pageHeight: 'pageHeight',
			roleName: 'authentication/getRole',
		}),
		activeFilterOptions () {
			return [
				{ text: this.$t('all'), value: null },
				{ text: this.$t('active'), value: true },
				{ text: this.$t('inactive'), value: false },
			];
		},
		groupedTerraMapping () {
			const filteredMappings =
				this.activeFilter === null
					? this.terraUserMapping
					: this.terraUserMapping.filter((mapping) => mapping.active === this.activeFilter);
			const groupedMappings = filteredMappings.reduce((locations, currentMapping) => {
				const {
					locationName,
					userName,
					locationId,
					userReferenceId,
					enableSendingDataToECP,
					ecpUserInpersonateId,
					...rest
				} = currentMapping;
				let locationGroup = locations.find((loc) => loc.locationName === locationName);
				if (!locationGroup) {
					locationGroup = {
						locationName,
						locationId,
						enableSendingDataToECP: null,
						ecpUserInpersonateId: 0,
						userNames: [],
					};
					locations.push(locationGroup);
				}
				let userGroup = locationGroup.userNames.find((user) => user.userName === userName);
				if (!userGroup) {
					userGroup = {
						userName,
						userReferenceId,
						locationId,
						enableSendingDataToECP: true,
						ecpUserInpersonateId: 0,
						mappings: [],
					};
					locationGroup.userNames.push(userGroup);
				}
				userGroup.enableSendingDataToECP = userGroup.enableSendingDataToECP && enableSendingDataToECP;
				userGroup.mappings.push({
					locationName,
					userName,
					locationId,
					userReferenceId,
					enableSendingDataToECP,
					ecpUserInpersonateId,
					...rest,
				});
				return locations;
			}, []);
			groupedMappings.forEach((location) => {
				let hasTrue = false;
				let hasFalse = false;
				for (const user of location.userNames) {
					if (user.enableSendingDataToECP) hasTrue = true;
					else hasFalse = true;
					if (hasTrue && hasFalse) break;
				}
				location.enableSendingDataToECP = hasTrue && hasFalse ? null : hasTrue;
				location.userNames.forEach((user) => {
					if (user.mappings.length > 0) {
						const firstId = user.mappings[0].ecpUserInpersonateId;
						const allSame = user.mappings.every((m) => m.ecpUserInpersonateId === firstId);
						user.ecpUserInpersonateId = allSame ? firstId : 0;
					}
				});
				const firstId = location.userNames[0].ecpUserInpersonateId;
				const allSame = location.userNames.every((u) => u.ecpUserInpersonateId === firstId);
				location.ecpUserInpersonateId = allSame ? firstId : 0;
			});
			return groupedMappings;
		},
		filteredTerraMapping () {
			const searchLower = this.search.toLowerCase();
			if (!this.selectedLocation) return [];
			if (!this.search) return this.selectedLocation.userNames;
			return this.selectedLocation.userNames.filter(
				(user) =>
					user.userName.toLowerCase().includes(searchLower) ||
					user.mappings.some((mapping) => mapping.terraUserId.toLowerCase().includes(searchLower)) ||
					user.mappings.some((mapping) => mapping.provider.toLowerCase().includes(searchLower)) ||
					user.mappings.some((mapping) =>
						this.findUserName(mapping.ecpUserInpersonateId, mapping.locationId).toLowerCase().includes(searchLower),
					),
			);
		},
		userNameGroupHeaders () {
			return [
				{ text: this.$t('patient-name'), value: 'userName' },
				{ text: '', value: '', align: 'end', class: 'pr-5', sortable: false },
			];
		},
		userIsHospitalStaff () {
			return ['HOSPITAL_ADMIN', 'DOCTOR', 'NURSE', 'CARE_GIVER'].includes(this.roleName);
		},
		itemHeaders () {
			return [
				{ text: this.$t('active'), value: 'active' },
				{ text: this.$t('terra-user-id'), value: 'terraUserId' },
				{ text: this.$t('provider'), value: 'provider' },
				{ text: this.$t('data-coordinator'), value: 'ecpUserImpersonateName' },
				{ text: this.$t('last-updated-at'), value: 'lastWebhookUpdate' },
				{ text: this.$t('created-at'), value: 'terraMappingCreatedAt' },
				{ text: this.$t('sending-data-to-ecp'), value: 'enableSendingDataToECP', align: 'center' },
			];
		},
	},
	async mounted () {
		await Promise.all([
			this.getTerraUserMappingTable(this.userIsHospitalStaff ? localStorage.selectedHospitalAssignedId : null),
			this.getECPUsersForTerraMappings(),
		]);
		this.loading = false;
	},
	methods: {
		...mapActions({
			getTerraUserMappingTable: 'terra/getTerraUserMappingTable',
			getECPUsersForTerraMappings: 'terra/getECPUsersForTerraMappings',
			setSendingDataFromTerraToECP: 'terra/setSendingDataFromTerraToECP',
			setECPUserInpersonateId: 'terra/setECPUserInpersonateId',
		}),
		formatTimestamp,
		findUserName (userId, locationId) {
			if (!userId) return '';
			const location = this.ecpUsers.find((loc) => loc.locationId === locationId);
			if (location) {
				const user = location.ecpUserForTerraDto.find((u) => u.id === userId);
				return user ? user.userName : '';
			}
			return '';
		},
		async flipSendingDataToECP (item) {
			this.loading = true;
			if (item.userReferenceId === undefined) {
				await this.setSendingDataFromTerraToECP({
					enableSendingDataToECP: item.enableSendingDataToECP,
					locationId: item.locationId,
					patientId: null,
				});
			} else {
				await this.setSendingDataFromTerraToECP({
					enableSendingDataToECP: item.enableSendingDataToECP,
					locationId: item.mappings[0].locationId,
					patientId: item.userReferenceId,
				});
			}
			if (this.userIsHospitalStaff) {
				await this.getTerraUserMappingTable(localStorage.selectedHospitalAssignedId);
			} else {
				await this.getTerraUserMappingTable();
			}
			this.loading = false;
		},
		async setECPUser (item) {
			this.loading = true;
			if (item.userReferenceId === undefined) {
				await this.setECPUserInpersonateId({
					ecpUserInpersonateId: item.ecpUserInpersonateId,
					locationId: item.locationId,
					patientId: null,
				});
			} else {
				await this.setECPUserInpersonateId({
					ecpUserInpersonateId: item.ecpUserInpersonateId,
					locationId: item.mappings[0].locationId,
					patientId: item.userReferenceId,
				});
			}
			if (this.userIsHospitalStaff) {
				await this.getTerraUserMappingTable(localStorage.selectedHospitalAssignedId);
			} else {
				await this.getTerraUserMappingTable();
			}
			this.loading = false;
		},
		openViewDialog (item) {
			this.selectedTerraMapping = item;
			this.showViewDialog = true;
		},
	},
	watch: {
		groupedTerraMapping (newList) {
			this.selectedLocation = newList[0] || null;
		},
	},
};
</script>
<style scoped>
.full-height-card {
	height: calc(100vh - 77px) !important;
}
</style>

<style>
.thin-select .v-input__control {
	min-height: 28px !important;
	height: 28px !important;
}
.thin-select .v-input__slot {
	min-height: 28px !important;
	height: 28px !important;
}
.thin-select .v-text-field__slot {
	min-height: 20px !important;
	height: 20px !important;
}
.thin-select .v-select__selection {
	line-height: 20px !important;
	margin-top: 0 !important;
	margin-bottom: 0 !important;
}
.thin-select .v-input__append-inner {
	margin-top: 2px !important;
	margin-bottom: 2px !important;
}
.thin-select .v-select__slot {
	min-height: 20px !important;
}
.thin-select .v-input__prepend-inner {
	margin-top: 6px !important;
}
</style>
